import { DELETING_SPEED_MS, PAUSE_AFTER_TYPED_MS, PAUSE_BEFORE_NEXT_MS, TYPING_SPEED_MS } from './constants';

export interface TypewriterProgress {
    phraseIndex: number;
    charCount: number;
    isDeleting: boolean;
}

export interface TypewriterOptions {
    typingSpeedMs?: number;
    deletingSpeedMs?: number;
    pauseAfterTypedMs?: number;
    pauseBeforeNextMs?: number;
    initialProgress?: TypewriterProgress;
}

export type TypewriterTick = (text: string, phraseIndex: number, progress: TypewriterProgress) => void;

export const runTypewriter = (phrases: string[], onTick: TypewriterTick, options: TypewriterOptions = {}) => {
    const {
        typingSpeedMs = TYPING_SPEED_MS,
        deletingSpeedMs = DELETING_SPEED_MS,
        pauseAfterTypedMs = PAUSE_AFTER_TYPED_MS,
        pauseBeforeNextMs = PAUSE_BEFORE_NEXT_MS,
        initialProgress,
    } = options;

    if (!phrases.length) {
        return () => undefined;
    }

    let phraseIndex = initialProgress ? initialProgress.phraseIndex : 0;
    let charCount = initialProgress ? initialProgress.charCount : 0;
    let isDeleting = Boolean(initialProgress?.isDeleting);
    let isCancelled = false;
    let timeout: ReturnType<typeof setTimeout>;

    const charactersOf = (index: number) => Array.from(phrases[index % phrases.length]);

    const emit = () => {
        const index = phraseIndex % phrases.length;
        const characters = charactersOf(index);
        const safeCharCount = Math.min(charCount, characters.length);

        charCount = safeCharCount;

        onTick(characters.slice(0, safeCharCount).join(''), index, { phraseIndex, charCount, isDeleting });
    };

    const step = () => {
        if (isCancelled) {
            return;
        }

        const characters = charactersOf(phraseIndex);

        if (!isDeleting && charCount >= characters.length) {
            isDeleting = true;
            timeout = setTimeout(step, pauseAfterTypedMs);

            return;
        }

        if (isDeleting && charCount === 0) {
            isDeleting = false;
            phraseIndex += 1;
            timeout = setTimeout(step, pauseBeforeNextMs);

            return;
        }

        charCount += isDeleting ? -1 : 1;

        emit();

        timeout = setTimeout(step, isDeleting ? deletingSpeedMs : typingSpeedMs);
    };

    emit();
    timeout = setTimeout(step, typingSpeedMs);

    return () => {
        isCancelled = true;
        clearTimeout(timeout);
    };
};
